// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: Customise Home > Sections. The Home sections in their order, each with a grip to
// drag it (touch or mouse; arrow keys on the focused grip) and a switch to show or hide it.
// Then how many rows a section shows before "+N more", how Konvo's suggestions sit, and
// whether Home opens straight into the focus deck when there is a lot to do.

import {
	type SettingsIconChoiceCardOption,
	SettingsIconChoiceCards,
} from '@app/features/app/components/dialogs/shared/SettingsIconChoiceCards';
import {SettingsSection} from '@app/features/app/components/dialogs/shared/SettingsSection';
import styles from '@app/features/konvo_today/components/settings/CustomiseHome.module.css';
import {
	HOME_ALL_CHATS_BY_CONNECTION_DESCRIPTION,
	HOME_SECTION_DESCRIPTION,
	HOME_SECTION_LABEL,
	HOME_SUGGESTION_STYLE_LABEL,
	homeSectionIcon,
} from '@app/features/konvo_today/components/settings/CustomiseHomeMeta';
import HomePreferences, {
	HOME_ROW_CHOICES,
	type HomeSectionId,
	type HomeSuggestionStyle,
} from '@app/features/konvo_today/state/HomePreferences';
import {Switch} from '@app/features/ui/components/form/FormSwitch';
import {
	DotsSixVerticalIcon,
	MinusIcon,
	PlusIcon,
	RowsIcon,
	StackSimpleIcon,
	TargetIcon,
} from '@app/features/ui/icons/Icons';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useEffect, useId, useRef, useState} from 'react';

const SECTIONS_DESCRIPTOR = msg({
	message: 'Sections · drag to reorder',
	comment: 'Customise Home section title above the Home sections list, which can be reordered by dragging.',
});
const REORDER_DESCRIPTOR = msg({
	message: 'Reorder {label}',
	comment: 'Accessible label for the drag handle of a Home section in Customise Home. {label} is the section name.',
});
const REORDER_HINT_DESCRIPTOR = msg({
	message: 'Drag, or use the up and down arrow keys, to move it.',
	comment: 'Screen-reader hint on the drag handle of a Home section in Customise Home.',
});
const MOVED_DESCRIPTOR = msg({
	message: '{label} moved to place {place} of {total}',
	comment: 'Screen-reader announcement after reordering a Home section. {label} is the section name.',
});
const SHOW_SECTION_DESCRIPTOR = msg({
	message: 'Show {label} on Home',
	comment: 'Accessible label for the switch that shows or hides a Home section.',
});
const ROWS_DESCRIPTOR = msg({
	message: 'Rows before “+N more”',
	comment: 'Customise Home section title: how many rows a Home section shows before a "+N more" link.',
});
const ALL_DESCRIPTOR = msg({
	message: 'All',
	comment: 'Customise Home > Rows before "+N more": show every row, never fold. Keep it short.',
});
const SUGGESTIONS_DESCRIPTOR = msg({
	message: 'Konvo suggestions',
	comment: "Customise Home section title: how the assistant's suggestions are shown on Home.",
});
const SUGGESTIONS_NOTE_STACK_DESCRIPTOR = msg({
	message: 'One card on top, the rest stacked behind it. Tap to fan them out.',
	comment: 'Note under the Konvo suggestions choice when Stack is picked.',
});
const SUGGESTIONS_NOTE_LIST_DESCRIPTOR = msg({
	message: 'Every suggestion as its own row.',
	comment: 'Note under the Konvo suggestions choice when List is picked.',
});
const SUGGESTIONS_ARIA_DESCRIPTOR = msg({
	message: "How Konvo's suggestions are shown",
	comment: 'Accessible label for the Konvo suggestions choice cards.',
});
const DECK_SECTION_DESCRIPTOR = msg({
	message: 'Focus deck',
	comment: 'Customise Home section title: the full-screen deck that goes through to-dos one at a time.',
});
const DECK_TITLE_DESCRIPTOR = msg({
	message: 'Open in the focus deck',
	comment: 'Customise Home: switch that opens Home straight in the focus deck when there is a lot to do.',
});
const DECK_ON_DESCRIPTOR = msg({
	message: 'When there are {count} or more things',
	comment: 'Line under "Open in the focus deck" while it is on. {count} is a number like 7.',
});
const DECK_OFF_DESCRIPTOR = msg({
	message: 'Off. Home opens as usual.',
	comment: 'Line under "Open in the focus deck" while it is off.',
});
const DECK_LAYOUT_DESCRIPTOR = msg({
	message: 'Home already is the focus deck in the Focus deck layout.',
	comment: 'Line under "Open in the focus deck" (switched off and greyed) while the Focus deck layout is picked.',
});
const DECK_COUNT_DESCRIPTOR = msg({
	message: 'Things to do',
	comment: 'Customise Home: label of the stepper that sets how many to-dos open the focus deck.',
});
const FEWER_DESCRIPTOR = msg({message: 'Fewer', comment: 'Accessible label for the minus button of a number stepper.'});
const MORE_DESCRIPTOR = msg({message: 'More', comment: 'Accessible label for the plus button of a number stepper.'});

export const HOME_DECK_MIN = 5;
export const HOME_DECK_MAX = 12;
const HOME_DECK_DEFAULT = 7;

interface DragInfo {
	id: HomeSectionId;
	from: number;
	to: number;
	dy: number;
	height: number;
}

interface DragTrack extends DragInfo {
	pointerId: number;
	startY: number;
	mids: Array<number>;
}

/** Where the dragged row lands for a given pull: past a neighbour's middle, it swaps with it. */
export function dropIndex(mids: ReadonlyArray<number>, from: number, dy: number): number {
	const centre = mids[from] + dy;
	let to = from;
	for (let index = from + 1; index < mids.length; index++) if (centre > mids[index]) to = index;
	for (let index = from - 1; index >= 0; index--) if (centre < mids[index]) to = index;
	return to;
}

function shiftFor(index: number, drag: DragInfo | null): number {
	if (!drag || index === drag.from) return drag ? drag.dy : 0;
	if (drag.from < drag.to && index > drag.from && index <= drag.to) return -drag.height;
	if (drag.to < drag.from && index >= drag.to && index < drag.from) return drag.height;
	return 0;
}

const SectionList = observer(() => {
	const {i18n} = useLingui();
	const hintId = useId();
	const sections = HomePreferences.orderedSections;
	const visibleCount = sections.filter((section) => section.visible).length;
	const listRef = useRef<HTMLDivElement>(null);
	const trackRef = useRef<DragTrack | null>(null);
	const grips = useRef(new Map<HomeSectionId, HTMLButtonElement>());
	const refocus = useRef<HomeSectionId | null>(null);
	const [drag, setDrag] = useState<DragInfo | null>(null);
	const [announcement, setAnnouncement] = useState('');

	const order = sections.map((section) => section.id).join();
	useEffect(() => {
		const id = refocus.current;
		refocus.current = null;
		if (id) grips.current.get(id)?.focus();
	}, [order]);

	const move = (id: HomeSectionId, to: number) => {
		const total = sections.length;
		const place = Math.max(0, Math.min(to, total - 1));
		HomePreferences.moveSection(id, place);
		setAnnouncement(i18n._(MOVED_DESCRIPTOR, {label: i18n._(HOME_SECTION_LABEL[id]), place: place + 1, total}));
	};

	const onPointerDown = (event: React.PointerEvent<HTMLButtonElement>, id: HomeSectionId, index: number) => {
		if (event.pointerType === 'mouse' && event.button !== 0) return;
		const rows = Array.from(listRef.current?.querySelectorAll<HTMLElement>('[data-home-section-row]') ?? []);
		const rects = rows.map((row) => row.getBoundingClientRect());
		if (!rects[index]) return;
		event.preventDefault();
		event.currentTarget.setPointerCapture?.(event.pointerId);
		const info: DragInfo = {id, from: index, to: index, dy: 0, height: rects[index].height};
		trackRef.current = {
			...info,
			pointerId: event.pointerId,
			startY: event.clientY,
			mids: rects.map((rect) => rect.top + rect.height / 2),
		};
		setDrag(info);
	};

	const onPointerMove = (event: React.PointerEvent<HTMLButtonElement>) => {
		const track = trackRef.current;
		if (!track || event.pointerId !== track.pointerId) return;
		const first = track.mids[0] - track.mids[track.from];
		const last = track.mids[track.mids.length - 1] - track.mids[track.from];
		const dy = Math.max(first, Math.min(last, event.clientY - track.startY));
		track.dy = dy;
		track.to = dropIndex(track.mids, track.from, dy);
		setDrag({id: track.id, from: track.from, to: track.to, dy, height: track.height});
	};

	const finish = (commit: boolean) => {
		const track = trackRef.current;
		trackRef.current = null;
		setDrag(null);
		if (commit && track && track.to !== track.from) move(track.id, track.to);
	};

	const onGripKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, id: HomeSectionId, index: number) => {
		const step = event.key === 'ArrowUp' ? -1 : event.key === 'ArrowDown' ? 1 : 0;
		if (step === 0) return;
		event.preventDefault();
		const to = index + step;
		if (to < 0 || to >= sections.length) return;
		refocus.current = id;
		move(id, to);
	};

	return (
		<>
			<div
				ref={listRef}
				className={clsx(styles.card, styles.sectionList, drag && styles.sectionListDragging)}
				data-flx="konvo-today.customise-home.sections"
			>
				{sections.map((section, index) => {
					const label = i18n._(HOME_SECTION_LABEL[section.id]);
					const lifted = drag?.id === section.id;
					const lastVisible = section.visible && visibleCount === 1;
					return (
						<div
							key={section.id}
							className={clsx(
								styles.row,
								styles.sectionRow,
								!section.visible && styles.rowOff,
								lifted && styles.rowLifted,
							)}
							style={drag ? {transform: `translateY(${shiftFor(index, drag)}px)`} : undefined}
							data-home-section-row={section.id}
							data-flx={`konvo-today.customise-home.section.${section.id}`}
						>
							<button
								ref={(node) => {
									if (node) grips.current.set(section.id, node);
									else grips.current.delete(section.id);
								}}
								type="button"
								className={styles.grip}
								aria-label={i18n._(REORDER_DESCRIPTOR, {label})}
								aria-describedby={hintId}
								onPointerDown={(event) => onPointerDown(event, section.id, index)}
								onPointerMove={onPointerMove}
								onPointerUp={() => finish(true)}
								onPointerCancel={() => finish(false)}
								onLostPointerCapture={() => trackRef.current && finish(true)}
								onKeyDown={(event) => onGripKeyDown(event, section.id, index)}
								data-flx={`konvo-today.customise-home.section.${section.id}.grip`}
							>
								<DotsSixVerticalIcon size={18} />
							</button>
							<span className={styles.tile} aria-hidden="true">
								{homeSectionIcon(section.id)}
							</span>
							<span className={styles.text}>
								<span className={styles.title}>{label}</span>
								<span className={styles.description}>
									{i18n._(
										section.id === 'all_chats' && HomePreferences.effectiveAllChatsGrouping === 'connection'
											? HOME_ALL_CHATS_BY_CONNECTION_DESCRIPTION
											: HOME_SECTION_DESCRIPTION[section.id],
									)}
								</span>
							</span>
							<Switch
								compact
								value={section.visible}
								disabled={lastVisible}
								onChange={(visible) => HomePreferences.setSectionVisible(section.id, visible)}
								ariaLabel={i18n._(SHOW_SECTION_DESCRIPTOR, {label})}
								data-flx={`konvo-today.customise-home.section.${section.id}.switch`}
							/>
						</div>
					);
				})}
			</div>
			<span id={hintId} className={styles.srOnly}>
				{i18n._(REORDER_HINT_DESCRIPTOR)}
			</span>
			<span className={styles.srOnly} aria-live="polite">
				{announcement}
			</span>
		</>
	);
});

const RowsChips = observer(() => {
	const {i18n} = useLingui();
	const current = HOME_ROW_CHOICES.includes(HomePreferences.rowsBeforeMore) ? HomePreferences.rowsBeforeMore : 3;
	return (
		<div className={clsx(styles.card, styles.chipsCard)}>
			<div
				className={styles.chips}
				role="radiogroup"
				aria-label={i18n._(ROWS_DESCRIPTOR)}
				data-flx="konvo-today.customise-home.rows"
			>
				{HOME_ROW_CHOICES.map((rows) => (
					<button
						key={rows}
						type="button"
						role="radio"
						aria-checked={rows === current}
						className={clsx(styles.chip, rows === current && styles.chipOn)}
						onClick={() => HomePreferences.setRowsBeforeMore(rows)}
						data-flx={`konvo-today.customise-home.rows.${rows}`}
					>
						{rows === 0 ? i18n._(ALL_DESCRIPTOR) : String(rows)}
					</button>
				))}
			</div>
		</div>
	);
});

const DeckCard = observer(() => {
	const {i18n} = useLingui();
	const titleId = useId();
	const threshold = HomePreferences.deckThreshold;
	// The Deck layout already is the deck: the switch waits, greyed out, keeping its number.
	const deckLayout = HomePreferences.effectiveLayout === 'deck';
	const on = threshold > 0 && !deckLayout;
	const [remembered, setRemembered] = useState(on ? threshold : HOME_DECK_DEFAULT);
	const count = on ? threshold : remembered;
	const set = (next: number) => {
		const value = Math.max(HOME_DECK_MIN, Math.min(HOME_DECK_MAX, next));
		setRemembered(value);
		HomePreferences.setDeckThreshold(value);
	};
	return (
		<div className={styles.card} data-flx="konvo-today.customise-home.deck">
			<div className={styles.row}>
				<span className={styles.tile} aria-hidden="true">
					<TargetIcon size={20} />
				</span>
				<span className={styles.text}>
					<span id={titleId} className={styles.title}>
						{i18n._(DECK_TITLE_DESCRIPTOR)}
					</span>
					<span className={styles.description}>
						{deckLayout
							? i18n._(DECK_LAYOUT_DESCRIPTOR)
							: on
								? i18n._(DECK_ON_DESCRIPTOR, {count})
								: i18n._(DECK_OFF_DESCRIPTOR)}
					</span>
				</span>
				<Switch
					compact
					value={on}
					disabled={deckLayout}
					onChange={(enabled) => (enabled ? set(count) : HomePreferences.setDeckThreshold(0))}
					ariaLabelledBy={titleId}
					data-flx="konvo-today.customise-home.deck.switch"
				/>
			</div>
			{on ? (
				<div className={clsx(styles.row, styles.stepperRow)}>
					<span className={styles.text}>
						<span className={styles.title}>{i18n._(DECK_COUNT_DESCRIPTOR)}</span>
					</span>
					<div className={styles.stepper} role="group" aria-label={i18n._(DECK_COUNT_DESCRIPTOR)}>
						<button
							type="button"
							className={styles.stepButton}
							disabled={count <= HOME_DECK_MIN}
							onClick={() => set(count - 1)}
							aria-label={i18n._(FEWER_DESCRIPTOR)}
							data-flx="konvo-today.customise-home.deck.fewer"
						>
							<MinusIcon size={16} />
						</button>
						<output className={styles.stepValue} aria-live="polite" data-flx="konvo-today.customise-home.deck.count">
							{count}
						</output>
						<button
							type="button"
							className={styles.stepButton}
							disabled={count >= HOME_DECK_MAX}
							onClick={() => set(count + 1)}
							aria-label={i18n._(MORE_DESCRIPTOR)}
							data-flx="konvo-today.customise-home.deck.more"
						>
							<PlusIcon size={16} />
						</button>
					</div>
				</div>
			) : null}
		</div>
	);
});

export const HomeSectionsSettings = observer(() => {
	const {i18n} = useLingui();
	const style = HomePreferences.suggestionStyle;
	const suggestionOptions: Array<SettingsIconChoiceCardOption<HomeSuggestionStyle>> = [
		{value: 'stack', name: i18n._(HOME_SUGGESTION_STYLE_LABEL.stack), icon: <StackSimpleIcon size={20} />},
		{value: 'list', name: i18n._(HOME_SUGGESTION_STYLE_LABEL.list), icon: <RowsIcon size={20} />},
	];
	return (
		<>
			<SettingsSection id="konvo-home-sections" title={i18n._(SECTIONS_DESCRIPTOR)} className={styles.bare}>
				<SectionList />
			</SettingsSection>
			<SettingsSection id="konvo-home-rows" title={i18n._(ROWS_DESCRIPTOR)} className={styles.bare}>
				<RowsChips />
			</SettingsSection>
			<SettingsSection id="konvo-home-suggestions" title={i18n._(SUGGESTIONS_DESCRIPTOR)} className={styles.bare}>
				<SettingsIconChoiceCards<HomeSuggestionStyle>
					options={suggestionOptions}
					value={style}
					onChange={HomePreferences.setSuggestionStyle}
					aria-label={i18n._(SUGGESTIONS_ARIA_DESCRIPTOR)}
					note={i18n._(style === 'list' ? SUGGESTIONS_NOTE_LIST_DESCRIPTOR : SUGGESTIONS_NOTE_STACK_DESCRIPTOR)}
					className={styles.iconChoices}
					data-flx="konvo-today.customise-home.suggestions"
				/>
			</SettingsSection>
			<SettingsSection id="konvo-home-deck" title={i18n._(DECK_SECTION_DESCRIPTOR)} className={styles.bare}>
				<DeckCard />
			</SettingsSection>
		</>
	);
});
